import * as React from "react";

import { mergeStyleSets } from "@fluentui/react";
import {
  IContextualMenuItem,
  IContextualMenuRenderItem,
  IMenuItemStyles,
} from "@fluentui/react/lib/ContextualMenu";
import { FocusZoneDirection } from "@fluentui/react/lib/FocusZone";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  getAppButtonMenuItem,
  getAppFlyoutAnchorMenuItem,
  getAppFlyoutAnchorMenuItemWithMemoization,
  getAppSplitButtonMenuItem,
  getAppSplitButtonMenuItemWithMemoization,
  getAppToggleButtonMenuItem,
  getClassNamesForFlyoutAnchorMenuButton,
} from "@1js/acui-button";
import { AppStrings, dataUniqueId, UILocation } from "@1js/acui-common";
import { AppFontStylesPreviewGalleryProps } from "@1js/acui-gallery-font-styles";
import { AppIconPreviewGalleryProps } from "@1js/acui-gallery-icon";
import {
  AppIcons,
  getSubmenuIconProps,
  iconNameToIconProps,
} from "@1js/acui-icons";
import { menuItemInMenuOnExecute } from "@1js/acui-keytip";
import { createIdForMemoization } from "@1js/acui-memoization";
import {
  ControlInMenuRendererFunction,
  convertMenuDefinitionToFabricSubMenuProps,
  deriveMenuKeytip,
  getDefaultMemoizedMenuItem,
  getDefaultMenuItem as getDefaultMenuItemBase,
  memoizedDismissMenu,
  MenuItemRenderComponent,
  RenderIContextualMenuItemCallback,
} from "@1js/acui-menu";

import { MenuItemControlProps } from "../controls/Menu";
import { RibbonTableStylesPreviewGalleryProps } from "./Controls/RibbonGallery";
import { getReactComponentForRibbonControlInMenu } from "./getReactComponentForRibbonControlInMenu";

export {
  MenuItemRenderComponent,
  getAppButtonMenuItem as getButtonMenuItem,
  getAppToggleButtonMenuItem as getToggleButtonMenuItem,
  getAppSplitButtonMenuItemWithMemoization as getSplitButtonMenuItemWithMemoization,
  getAppSplitButtonMenuItem as getSplitButtonMenuItem,
  getAppFlyoutAnchorMenuItem as getFlyoutAnchorMenuItem,
  getAppFlyoutAnchorMenuItemWithMemoization as getFlyoutAnchorMenuItemWithMemoization,
  memoizedDismissMenu,
  getDefaultMemoizedMenuItem,
  RenderIContextualMenuItemCallback,
};

const customButtonMenuItemStyles = { secondaryText: { paddingLeft: "20px" } };

export function getGalleryMenuItem(
  controlProps:
    | RibbonTableStylesPreviewGalleryProps
    | AppIconPreviewGalleryProps
    | AppFontStylesPreviewGalleryProps,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  appIcons: AppIcons,
  customMenuItemStyles?: Partial<IMenuItemStyles>
): IContextualMenuItem {
  // Preview galleries should be converted to flyout anchors in contextual menus
  const contextualItem = React.createRef<IContextualMenuRenderItem>();
  return {
    type: "AppFlyoutAnchorProps",
    key: controlProps.id,
    name:
      (controlProps.flyoutAnchorProps &&
        controlProps.flyoutAnchorProps.label) ||
      controlProps.menuLauncherLabel,
    subMenuProps: {
      ...convertMenuDefinitionToFabricSubMenuProps({
        menuDefinition: controlProps.expandedOptions,
        controlInMenuRendererFunction,
        location: controlProps.location,
      }),
      onMenuOpened: (): void =>
        controlProps.onMenuButtonExecute &&
        controlProps.onMenuButtonExecute({
          id: controlProps.id,
          location: controlProps.location,
        }),
      onMenuDismissed: (): void =>
        controlProps.onAfterMenuDismiss &&
        controlProps.onAfterMenuDismiss(controlProps.id),
    },
    submenuIconProps: getSubmenuIconProps(appIcons),
    arrowDirection: FocusZoneDirection.bidirectional,
    disabled: controlProps.disabled,
    getItemClassNames: getClassNamesForFlyoutAnchorMenuButton(
      mergeStyleSets(
        customMenuItemStyles,
        controlProps.flyoutAnchorProps && controlProps.flyoutAnchorProps.styles
      )
    ),
    iconProps: iconNameToIconProps(
      controlProps.flyoutAnchorProps && controlProps.flyoutAnchorProps.icon
    ),
    keytipProps:
      controlProps.keytipProps &&
      deriveMenuKeytip(
        controlProps.keytipProps,
        (executeTarget: HTMLElement) => {
          menuItemInMenuOnExecute(executeTarget, contextualItem);
        }
      ),
    componentRef: contextualItem,
    "data-unique-id": dataUniqueId(controlProps),
  };
}
export function getDefaultMenuItem(
  controlProps: MenuItemControlProps,
  appIcons?: AppIcons
): IContextualMenuItem {
  return getDefaultMenuItemBase(
    controlProps,
    getReactComponentForRibbonControlInMenu,
    appIcons,
    getGallerySectionLength
  );
}

export function combineControlExecutes<T extends MenuItemControlProps>(
  controlProps: T,
  onMenuItemExecuted?: (id: string) => void
): T {
  // Check if we have a handler that needs to trigger on menu item executes. If so call it before we call the control onExecute
  if (!onMenuItemExecuted) {
    return controlProps;
  }

  // Do not hook AppFlyoutAnchor as it does not execute anything
  // and may be triggered accidentally on mouse hoovering number of times.
  // Command executed on the sub-menu which flyout hosts will be still recorded.
  // We would know it came from the submenu based on the parent id set.
  if (controlProps.type === "AppFlyoutAnchorProps") {
    return controlProps;
  }

  if ("onExecute" in controlProps) {
    const onExecute = controlProps.onExecute;
    if (onExecute) {
      return {
        ...controlProps,
        onExecute: (args: any) => {
          // Some controls may be firing the execute event during shadow rendering.
          // We do not want that to be propagated and logged. We'll check isShadowRendering
          // property defined on MenuOpenOnExecuteParameter to bypass it.
          if (!args.isShadowRendering) {
            onMenuItemExecuted(controlProps.id);
          }
          onExecute.apply(controlProps, [args]);
        },
      };
    }
  } else if ("onGalleryButtonExecute" in controlProps) {
    // We have some gallery buttons accessible via floatie and they need special treatment.
    const onGalleryButtonExecute = controlProps.onGalleryButtonExecute;
    if (onGalleryButtonExecute) {
      return {
        ...controlProps,
        onGalleryButtonExecute: (args: any) => {
          if (!args.isShadowRendering) {
            onMenuItemExecuted(controlProps.id);
          }
          onGalleryButtonExecute.apply(controlProps, [args]);
        },
      };
    }
  }

  return controlProps;
}

export const getDefaultRibbonControlInMenuRendererFunction = memoizeFunction(
  (
    appIcons: AppIcons,
    appStrings: AppStrings
  ): ControlInMenuRendererFunction => {
    return (params): IContextualMenuItem[] => {
      const {
        controlProps,
        location,
        onSubMenuPositioned,
        arePerfImprovementsEnabled,
        onMenuItemExecuted,
        shouldMergeStylesIntoMenuItems,
      } = params;

      const filteredControls = controlProps.filter(
        (x) => getGallerySectionLength(x as MenuItemControlProps) > 0
      );

      // TASK 2547546
      // We need to evaluate how other controls besides the ribbon will deal with focus
      // as we might have to refactor the models for us to set shouldTakeFocus to avoid setting
      // the values here
      return filteredControls.map((controlProps) => {
        const props = {
          ...combineControlExecutes(
            controlProps as MenuItemControlProps,
            onMenuItemExecuted
          ),
          location,
          appStrings,
        } as MenuItemControlProps;
        switch (props.type) {
          case "RibbonTableStylesPreviewGalleryProps":
          case "AppIconPreviewGalleryProps":
          case "AppFontStylesPreviewGalleryProps":
            return getGalleryMenuItem(
              props,
              getDefaultRibbonControlInMenuRendererFunction(
                appIcons,
                appStrings
              ),
              appIcons,
              undefined /* customMenuItemStyles */
            );
          case "AppButtonProps":
            return getAppButtonMenuItem(
              createIdForMemoization(
                props.id,
                props.location,
                UILocation.SingleLineRibbon.toString()
              ),
              {
                ...props,
                shouldTakeFocus:
                  props.shouldTakeFocus !== undefined
                    ? props.shouldTakeFocus
                    : true,
              },
              customButtonMenuItemStyles,
              undefined /* customProps */,
              undefined /* menuItemRenderer */,
              shouldMergeStylesIntoMenuItems
            );
          case "AppInkButtonProps":
          case "AppToggleButtonProps":
            return getAppToggleButtonMenuItem(
              {
                ...props,
                shouldTakeFocus:
                  props.shouldTakeFocus !== undefined
                    ? props.shouldTakeFocus
                    : true,
                type: "AppToggleButtonProps",
              },
              customButtonMenuItemStyles /* customMenuItemStyles */,
              undefined /* menuItemRenderer */,
              shouldMergeStylesIntoMenuItems
            );
          case "AppFlyoutAnchorProps":
          case "AppInkFlyoutAnchorProps":
            return getAppFlyoutAnchorMenuItem(
              createIdForMemoization(
                props.id,
                props.location,
                UILocation.SingleLineRibbon.toString()
              ),
              {
                ...props,
                type: "AppFlyoutAnchorProps",
              },
              getDefaultRibbonControlInMenuRendererFunction(
                appIcons,
                appStrings
              ),
              appIcons,
              onSubMenuPositioned,
              undefined /* rootMenuItemStyles */,
              undefined /* subMenuItemStyles */,
              arePerfImprovementsEnabled,
              onMenuItemExecuted,
              shouldMergeStylesIntoMenuItems
            );
          case "AppSplitButtonProps":
            return getAppSplitButtonMenuItem(
              createIdForMemoization(
                props.id,
                props.location,
                UILocation.SingleLineRibbon.toString()
              ),
              { ...props, shouldTakeFocus: true },
              getDefaultRibbonControlInMenuRendererFunction(
                appIcons,
                appStrings
              ),
              appIcons,
              onSubMenuPositioned,
              undefined /* rootMenuItemStyles */,
              undefined /* subMenuItemStyles */,
              arePerfImprovementsEnabled,
              onMenuItemExecuted
            );
          case "AppToggleProps":
          case "AppSpinButtonProps":
          case "AppCheckboxProps":
          case "AppComboBoxProps":
          case "AppColorPickerProps":
          case "RibbonInsertTableProps":
          case "RibbonChangeColorsFullGalleryProps":
          case "AppCharacterCodeGalleryProps":
          case "RibbonTableStylesFullGalleryProps":
          case "AppListGalleryProps":
          case "AppIconFullGalleryProps":
          case "AppFontStylesFullGalleryProps":
          case "AppInkThicknessFullGalleryProps":
          case "AppCompoundButtonProps":
          case "RibbonPageNumberFullGalleryProps":
          case "AppSliderProps":
          case "SingleLine":
          case "Multiline":
          case "AppInputProps":
          case "AppPersonaButtonProps":
          case "AppFontPickerProps":
          case "AppSearchBoxProps":
            return getDefaultMenuItem(props, appIcons);
          default: {
            throw new Error(
              `Ribbon controls of type ${props.type} are not valid as a control in a ribbon menu`
            );
          }
        }
      });
    };
  }
);

export function getGallerySectionLength(prop: MenuItemControlProps): number {
  switch (prop.type) {
    case "RibbonChangeColorsFullGalleryProps":
    case "AppCharacterCodeGalleryProps":
    case "RibbonTableStylesFullGalleryProps":
    case "AppListGalleryProps":
    case "RibbonPageNumberFullGalleryProps":
    case "AppIconFullGalleryProps":
    case "AppFontStylesFullGalleryProps":
      return prop.gallerySections ? prop.gallerySections.length : 0;
    default:
      // if the prop is not a gallery (combobox, spin button, etc), just count the component itself.
      return 1;
  }
}
